import { useNavigation } from "@react-navigation/native";
import {
  HStack,
  SearchBar,
  Stack,
  VStack,
  View,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { useEffect, useMemo, useState } from "react";
import { useScheduler } from "../../../../../../../../utilities/scheduler";
import { StockSelectionType } from "../../../../../../../../utilities/stock-count";
import {
  useCurrentCountContext,
  useStockCountSearchContext,
} from "../../../../../../../../utilities/stock-count/hooks";
import { useStockCountScreenContextProvider } from "../../../../hooks";
import { BarcodeComponent } from "../../../BarcodeComponent";
import { ensuredFocusProps } from "../../constants";
import {
  ScanNextContent,
  StandardCountContent,
  StockCountFooterModal,
} from "./components";
import { useWatch } from "react-hook-form";
import CountedItemSummaryCard from "../../../../../../components/CountedItemSummaryCard/CountedItemSummaryCard";
import { useWindowDimensions } from "react-native";
import { SMALL_SCREEN_BREAKPOINT } from "../../../../../../common";

export type StockCountFooterProps = {};

export function StockCountFooter(props: StockCountFooterProps) {
  const { schedule } = useScheduler(250);
  const layout = useWindowDimensions();

  const { navigate } = useNavigation<any>();

  const { spacings } = useThemeProvider();

  const { stockSelection, stockAdjuster, resetSelections } =
    useCurrentCountContext();

  const { stockCount } = useStockCountScreenContextProvider();

  const { fullTextSearch, scanMode, setShowProductView } =
    useStockCountSearchContext();

  const {
    methods,
    searchInputRef,
    selection,
    setSearchFocused,
    handleOnSubmitSearch,
  } = stockAdjuster;

  const [_, setShowHistoryContent] = useState(false);

  const { selectedStockCountLine } = useCurrentCountContext();

  const counted = useWatch({ control: methods.control, name: "counted" });

  const shouldShowScanNext = useMemo(() => {
    return (
      stockSelection?.selectionType === StockSelectionType.CAMERA_SCANNER &&
      scanMode !== "quick"
    );
  }, [stockSelection, scanMode]);

  const [showScanNext, setShowScanNext] = useState(shouldShowScanNext);

  function handleSearchBarOnChange(newQuery: string) {
    if (scanMode === "slow" || !counted) {
      resetSelections();
    }

    fullTextSearch.setQuery(newQuery);

    if (newQuery?.length) {
      setShowProductView(true);
    }
  }

  function handleOpenBarcodeScanner() {
    navigate("StockCountScannerScreen", {
      stockCountId: stockCount.id,
    });
  }

  function handleShowHistoryContent() {
    setShowHistoryContent(true);
  }

  function handleHideHistoryContent() {
    setShowHistoryContent(false);
  }

  function handleClear() {
    resetSelections();
    searchInputRef.current?.focus();
  }

  const rightComponent = fullTextSearch.query ? undefined : (
    <BarcodeComponent onPress={handleOpenBarcodeScanner} />
  );

  const searchBarMarkup = (
    <SearchBar
      onChange={handleSearchBarOnChange}
      value={fullTextSearch.query}
      placeholder="Search..."
      onFocused={() => setSearchFocused(true)}
      onBlurred={() => {
        // Blur will trigger before the onPress of the product list item
        schedule(() => setSearchFocused(false));
      }}
      onSubmitEditing={handleOnSubmitSearch}
      onCleared={handleClear}
      ref={searchInputRef as any}
      selection={selection}
      autoFocus
      containerStyle={{ height: 60 }}
      {...ensuredFocusProps}
      {...{ rightComponent }}
    />
  );

  useEffect(() => {
    setShowScanNext(shouldShowScanNext);
  }, [shouldShowScanNext]);

  const handleOnSummaryCardPress = (stockCountLineId: string) => {
    navigate("StockCountItemHistoryRootScreen", {
      screen: "StockCountItemHistoryScreen",
      stockCountLineId,
    });
  };

  return (
    <StockCountFooterModal
      onCompact={handleHideHistoryContent}
      onPreview={handleShowHistoryContent}
      onFullscreen={handleShowHistoryContent}
    >
      <VStack style={{ padding: spacings.s1 }}>
        {selectedStockCountLine && (
          <CountedItemSummaryCard
            compact={layout.width < SMALL_SCREEN_BREAKPOINT}
            stockCountLineItem={selectedStockCountLine}
            onPress={() =>
              selectedStockCountLine &&
              handleOnSummaryCardPress(selectedStockCountLine.id)
            }
          />
        )}
        {layout.width < SMALL_SCREEN_BREAKPOINT ? (
          <Stack vertical spacing={spacings.s2}>
            <View style={{ display: "flex", flex: 1, flexDirection: "row" }}>
              {searchBarMarkup}
            </View>
            {showScanNext ? (
              <ScanNextContent
                stockCountId={stockCount.id}
                onDismiss={() => setShowScanNext(false)}
              />
            ) : (
              <Stack fill spacing={spacings.s1}>
                <StandardCountContent stockCountId={stockCount.id} />
              </Stack>
            )}
          </Stack>
        ) : (
          <HStack fill spacing={spacings.s2}>
            <View style={{ display: "flex", flex: 1, flexDirection: "row" }}>
              {searchBarMarkup}
            </View>
            {showScanNext ? (
              <ScanNextContent
                stockCountId={stockCount.id}
                onDismiss={() => setShowScanNext(false)}
              />
            ) : (
              <StandardCountContent compact stockCountId={stockCount.id} />
            )}
          </HStack>
        )}
      </VStack>
    </StockCountFooterModal>
  );
}
